import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/data";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Story", href: "#story" },
  { label: "Partners", href: "#partners" },
  { label: "Track", href: "#track" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink/80 backdrop-blur-xl border-b border-gold/15 py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt="Peace Trucking" className="h-11 w-11 object-contain" />
          <div className="leading-none">
            <div className="font-serif text-xl tracking-wide text-cream">Peace Trucking</div>
            <div className="text-[10px] tracking-[0.35em] text-gold/70 uppercase">LLC · Est. Purpose</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm tracking-wide text-cream/80 hover:text-cream link-underline"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={BRAND.phoneHref}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm text-cream/80 hover:text-gold transition-colors"
          >
            <Phone size={15} className="text-gold" /> {BRAND.phone}
          </a>
          <button
            onClick={() => navigate(user ? "/dashboard" : "/login")}
            data-testid="nav-portal"
            className="btn-gold border border-gold/50 text-gold px-5 py-2 text-sm tracking-wide"
          >
            {user ? "Dashboard" : "Client Portal"}
          </button>
        </div>

        <button
          className="lg:hidden text-cream"
          onClick={() => setOpen(true)}
          data-testid="nav-menu-open"
          aria-label="Open menu"
        >
          <Menu size={26} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex justify-end p-6">
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X size={28} className="text-cream" />
              </button>
            </div>
            <div className="flex flex-col items-center gap-8 mt-10">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => go(l.href)}
                  className="font-serif text-3xl text-cream"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate(user ? "/dashboard" : "/login");
                }}
                className="mt-4 border border-gold text-gold px-8 py-3 tracking-widest"
              >
                {user ? "DASHBOARD" : "CLIENT PORTAL"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
